///
/// EarthBrowser
///
/// Hurricane legend body: the Saffir-Simpson intensity ramp on domes,
/// tracks and forecast points, plus the cone, wind extent rings and NHC
/// watch/warning coastlines. Colors come from stormDisplay.ts (CSS twins
/// of DesignColors.STORM_*).
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { cStormCategoryHex, cTropicalStormHex, cWatchWarningStyle } from "@/earthbrowser/dataset-distribution/hurricanes/stormDisplay"
import { LegendFootnote, LegendSection, SymbolRow } from "./LegendPrimitives"

// Intensity steps in display order: depression, tropical storm, Cat 1-5
const cIntensitySteps: Array<{ label: string; color: string }> = [
	{ label: "TD", color: cStormCategoryHex[0] },
	{ label: "TS", color: cTropicalStormHex },
	...[1, 2, 3, 4, 5].map(category => ({ label: `${category}`, color: cStormCategoryHex[category] })),
]

// Wind extent rings, outermost first (DesignColors.STORM_WIND_*)
const cWindRings = [
	{ color: "rgba(255, 214, 79, 0.45)", fill: "rgba(255, 214, 79, 0.08)" },
	{ color: "rgba(255, 140, 38, 0.5)", fill: "rgba(255, 140, 38, 0.12)" },
	{ color: "rgba(242, 51, 51, 0.55)", fill: "rgba(242, 51, 51, 0.16)" },
]

/** Discrete intensity ramp: one block per step with its label under it */
function IntensityRamp() {
	return (
		<div className="space-y-1">
			<div className="text-slate-300">Intensity · domes and tracks</div>
			<div className="flex gap-px rounded-sm overflow-hidden border border-white/20">
				{cIntensitySteps.map(step => (
					<span key={step.label} className="flex-1 h-2.5" style={{ backgroundColor: step.color }} />
				))}
			</div>
			<div className="flex text-[10px] text-slate-400">
				{cIntensitySteps.map(step => (
					<span key={step.label} className="flex-1 text-center">{step.label}</span>
				))}
			</div>
		</div>
	)
}

/** Dim observed run into a bright forecast run */
function TrackSwatch() {
	return (
		<svg width="36" height="10" viewBox="0 0 36 10" aria-hidden="true">
			<path d="M1 7 C 8 7, 12 3, 18 4" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeLinecap="round" />
			<path d="M18 4 C 24 5, 30 2, 35 2" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2.5" strokeLinecap="round" />
		</svg>
	)
}

function ConeSwatch() {
	return (
		<svg width="36" height="14" viewBox="0 0 36 14" aria-hidden="true">
			<path d="M2 7 L 30 1 A 6 6 0 0 1 30 13 Z" fill="rgba(255,255,255,0.09)" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
		</svg>
	)
}

function WindRingsSwatch() {
	return (
		<svg width="36" height="18" viewBox="0 0 36 18" aria-hidden="true">
			{cWindRings.map((ring, i) => (
				<circle key={i} cx="18" cy="9" r={8 - i * 2.5} fill={ring.fill} stroke={ring.color} strokeWidth="1.2" />
			))}
		</svg>
	)
}

/** Saturated warning beside its lighter watch */
function WatchWarningSwatch({ warning, watch }: { warning: string; watch: string }) {
	return (
		<svg width="36" height="8" viewBox="0 0 36 8" aria-hidden="true">
			<rect x="0" y="1" width="17" height="6" rx="1.5" fill={warning} />
			<rect x="19" y="1" width="17" height="6" rx="1.5" fill={watch} />
		</svg>
	)
}

export function HurricaneLegendContent() {
	return (
		<>
			<IntensityRamp />
			<LegendSection>
				<SymbolRow swatch={<span className="w-3 h-3 rounded-full" style={{ backgroundColor: "rgb(217, 28, 41)", boxShadow: "0 0 5px rgba(217, 28, 41, 0.8)" }} />}>
					Current position (spinning symbol)
				</SymbolRow>
				<SymbolRow swatch={<TrackSwatch />}>Track · dim past, bright forecast</SymbolRow>
				<SymbolRow swatch={<ConeSwatch />}>Forecast cone of uncertainty</SymbolRow>
				<SymbolRow swatch={<WindRingsSwatch />}>34 / 50 / 64 kt wind extent</SymbolRow>
			</LegendSection>
			<LegendSection>
				<SymbolRow swatch={<WatchWarningSwatch warning={cWatchWarningStyle.HWR.hex} watch={cWatchWarningStyle.HWA.hex} />}>
					Hurricane warning · watch
				</SymbolRow>
				<SymbolRow swatch={<WatchWarningSwatch warning={cWatchWarningStyle.TWR.hex} watch={cWatchWarningStyle.TWA.hex} />}>
					Tropical storm warning · watch
				</SymbolRow>
			</LegendSection>
			<LegendFootnote>
				NHC and JTWC advisories. Faint blue spaghetti is the ECMWF ensemble; click a storm for its forecast and landfall.
			</LegendFootnote>
		</>
	)
}
